import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Building2,
  Users,
  Package,
  TrendingUp,
  Activity,
  Shield,
  Database,
  BarChart3,
  Download,
  DollarSign,
} from "lucide-react";
import { orderService, getAnalyticsData } from "../../api/orderService";
import { getAllPharmacies, getAllDrivers, getRiderStats } from "../../api/pharmacyService";
import { analyticsService } from "../../api/analyticsService";
import { deliveryService } from "../../api/deliveryService";
import { reportsService } from "../../api/reportsService";
import { toast } from "sonner";
import GlobalLoader from "../../components/ui/GlobalLoader";
import { useAppSelector } from "@/redux/hooks";
import axiosInstance from "@/api/axiosInstance";
import { TabsContent } from "@radix-ui/react-tabs";
import { ChartContainer } from "@/components/ui/chart";
import { Bar, CartesianGrid, ComposedChart, XAxis, YAxis } from "recharts";



import {
  Tabs,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";


import {
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
} from "../../components/ui/chart";
import {
  BarChart,
  LineChart,
  Line,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
 
  
} from "recharts";
import {
  Clock,
  Truck,
  CheckCircle,
  Star,
  Target,
  Zap,
  Heart,
  Pill,
  ShoppingCart,

} from "lucide-react";

interface AdminStats {
  totalPharmacies: number;
  activePharmacies: number;
  totalDrivers: number;
  activeDrivers: number;
  totalOrders: number;
  todayOrders: number;
  systemHealth: number;
  platformRevenue: number;
}

export function AdminDashboardPage() {
  const [adminStats, setAdminStats] = useState<AdminStats>({
    totalPharmacies: 0,
    activePharmacies: 0,
    totalDrivers: 0,
    activeDrivers: 0,
    totalOrders: 0,
    todayOrders: 0,
    systemHealth: 0,
    platformRevenue: 0,
  });
  const [loading, setLoading] = useState(true);
  const [recentActivity, setRecentActivity] = useState<any[]>([]);
  const [topPerformingPharmacies, setTopPerformingPharmacies] = useState<any[]>([]);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportType, setReportType] = useState<'orders' | 'routes' | 'audit'>('orders');
  const [reportFormat, setReportFormat] = useState<'csv' | 'pdf'>('csv');
  const [generatingReport, setGeneratingReport] = useState(false);

  // Fetch admin dashboard data
  const fetchAdminData = async () => {
    try {
      setLoading(true);
      
      // Fetch all data in parallel
      const [analyticsData, pharmaciesData, drivers, deliveryStats, riderStats] = await Promise.all([
        analyticsService.getAllAnalytics(),
        getAllPharmacies(),
        getAllDrivers(),
        deliveryService.getDeliveryStats(),
        getRiderStats()
      ]);

      // Extract pharmacies array from the response
      const pharmacies = pharmaciesData.pharmacies || [];

      // Calculate stats
      const activePharmacies = pharmacies.filter(p => p.isActive).length;
      const activeDrivers = drivers.filter(d => d.status === 'online' || d.status === 'on-delivery').length;
      
      setAdminStats({
        totalPharmacies: pharmacies.length,
        activePharmacies,
        totalDrivers: drivers.length,
        activeDrivers,
        totalOrders: analyticsData.basicStats.totalOrders,
        todayOrders: analyticsData.basicStats.todayOrders,
        systemHealth: 99.8, // TODO: Get from system health API
        platformRevenue: analyticsData.basicStats.totalRevenue,
      });

      // Set recent activity (mock for now - should come from audit logs)
      setRecentActivity([
        {
          id: 1,
          action: "New pharmacy registered",
          time: "2 hours ago",
          type: "pharmacy",
        },
        {
          id: 2,
          action: "Driver completed deliveries",
          time: "3 hours ago",
          type: "driver",
        },
        {
          id: 3,
          action: "System maintenance completed",
          time: "5 hours ago",
          type: "system",
        },
        {
          id: 4,
          action: "Analytics report generated",
          time: "1 day ago",
          type: "analytics",
        },
      ]);

      // Set top performing pharmacies
      setTopPerformingPharmacies(
        pharmacies
          .sort((a, b) => b.totalOrders - a.totalOrders)
          .slice(0, 4)
          .map(pharmacy => ({
            name: pharmacy.name,
            orders: pharmacy.totalOrders,
            revenue: pharmacy.totalOrders * 50 // Estimate
          }))
      );

    } catch (error) {
      console.error('Error fetching admin data:', error);
      toast.error('Failed to load admin dashboard data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdminData();
  }, []);

  // Handle generate report
  const handleGenerateReport = async () => {
    try {
      setGeneratingReport(true);
      console.log(`Generating ${reportType} report in ${reportFormat} format...`);

      let blob: Blob;
      
      switch (reportType) {
        case 'orders':
          blob = await reportsService.exportOrdersReport(reportFormat as any);
          break;
        case 'routes':
          blob = await reportsService.exportRoutesReport(reportFormat as any);
          break;
        case 'audit':
          blob = await reportsService.exportAuditReport(reportFormat as any);
          break;
        default:
          throw new Error('Invalid report type');
      }

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${reportType}-report-${new Date().toISOString().split('T')[0]}.${reportFormat}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success(`${reportType.charAt(0).toUpperCase() + reportType.slice(1)} report downloaded successfully`);
      setReportDialogOpen(false);
    } catch (error: any) {
      console.error('Error generating report:', error);
      
      // Check if backend API is not implemented
      if (error?.response?.status === 404) {
        toast.error('Report generation API not implemented yet', {
          description: 'Please contact backend team to implement report endpoints'
        });
      } else {
        toast.error('Failed to generate report', {
          description: error?.response?.data?.message || 'Please try again later'
        });
      }
    } finally {
      setGeneratingReport(false);
    }
  };


  const user = useAppSelector((state) => state.auth.user);
  const [timeRange, setTimeRange] = useState("30d");
  const [error, setError] = useState(null);

  // Utility function to check if data exists (including zero values)
  const hasData = (data, key = null) => {
    if (!data || !Array.isArray(data) || data.length === 0) return false;
    if (!key) return true;
    // Allow zero values, just check if the key exists
    return data.some(item => item.hasOwnProperty(key) && item[key] !== null && item[key] !== undefined);
  };
  
  // State for analytics data
  const [deliveryInsightsData, setDeliveryInsightsData] = useState([]);
  const [patientDemographics, setPatientDemographics] = useState([]);
  const [revenueData, setRevenueData] = useState([]);
  const [orderStatusData, setOrderStatusData] = useState([]);
  const [medicationCategories, setMedicationCategories] = useState([]);
  const [satisfactionData, setSatisfactionData] = useState([]);
  const [hourlyData, setHourlyData] = useState([]);
  const [deliveryTypeBreakdown, setDeliveryTypeBreakdown] = useState(null);
  const [customerRetention, setCustomerRetention] = useState(null);
  const [equipmentUsage, setEquipmentUsage] = useState(null);
  const [growthMetrics, setGrowthMetrics] = useState(null);
  const [operationalKPIs, setOperationalKPIs] = useState(null);

  const [overallStats, setOverallStats] = useState(null);
  const [pharmacyStats, setPharmacyStats] = useState(null);
  const [pharmacyAnalytics, setPharmacyAnalytics] = useState([]);
  const [pharmacyRevenue, setPharmacyRevenue] = useState([]);
  const [pharmacyGrowth, setPharmacyGrowth] = useState([]);
  const [pharmacyPerformance, setPharmacyPerformance] = useState([]);
  const [pharmacyData, setPharmacyData] = useState([]);
  
  const defaultChartConfig = {
    deliveries: { label: "Deliveries", color: "#3b82f6" },
    successRate: { label: "Success Rate", color: "#10b981" },
    avgTime: { label: "Avg Time (min)", color: "#f59e0b" },
    revenue: { label: "Revenue", color: "#3b82f6" },
    expenses: { label: "Expenses", color: "#ef4444" },
    profit: { label: "Profit", color: "#10b981" },
  };
  
  const [chartConfig, setChartConfig] = useState(defaultChartConfig);

  // Fetch analytics data from API
  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axiosInstance.get(`/admin/pharmacies/all?timeRange=${timeRange}`);
      console.log('Full API Response:', response.data);
      // const data = await analyticsService.getAllAnalytics();

      // Extract the nested data object from response
      const analyticsData = response.data.data;
      console.log('Extracted analytics data:', analyticsData);
      
      // Update all state with API data
      setDeliveryInsightsData(analyticsData.deliveryInsights || []);
      setPatientDemographics(analyticsData.patientDemographics || []);
      setRevenueData(analyticsData.revenueData || []);
      setOrderStatusData(analyticsData.orderStatus || []);
      setMedicationCategories(analyticsData.medicationCategories || []);
      setSatisfactionData(analyticsData.satisfactionData || []);
      setHourlyData(analyticsData.hourlyData || []);
      setDeliveryTypeBreakdown(analyticsData.deliveryTypeBreakdown || null);
      setCustomerRetention(analyticsData.customerRetention || null);
      setEquipmentUsage(analyticsData.equipmentUsage || null);
      setGrowthMetrics(analyticsData.growthMetrics || null);
      setOperationalKPIs(analyticsData.operationalKPIs || null);
      
      // Map NEW comprehensive pharmacy analytics data
      setOverallStats(analyticsData.overallStats || null);
      setPharmacyStats(analyticsData.pharmacyStats || null);
      setPharmacyAnalytics(analyticsData.pharmacyAnalytics || []);
      setPharmacyRevenue(analyticsData.pharmacyRevenue || []);
      setPharmacyGrowth(analyticsData.pharmacyGrowth || []);
      setPharmacyPerformance(analyticsData.pharmacyPerformance || []);
      setPharmacyData(analyticsData.pharmacyData || []);
      setChartConfig(analyticsData.chartConfig || {
        deliveries: { label: "Deliveries", color: "#3b82f6" },
        successRate: { label: "Success Rate", color: "#10b981" },
        avgTime: { label: "Avg Time (min)", color: "#f59e0b" },
        revenue: { label: "Revenue", color: "#3b82f6" },
        expenses: { label: "Expenses", color: "#ef4444" },
        profit: { label: "Profit", color: "#10b981" }
      });
      
      console.log('State updated with data lengths:', {
        deliveryInsights: analyticsData.deliveryInsights?.length || 0,
        patientDemographics: analyticsData.patientDemographics?.length || 0,
        revenueData: analyticsData.revenueData?.length || 0,
        orderStatus: analyticsData.orderStatus?.length || 0,
        medicationCategories: analyticsData.medicationCategories?.length || 0,
        satisfactionData: analyticsData.satisfactionData?.length || 0
      });
      
      console.log('Data validation results:', {
        hasDeliveryData: hasData(analyticsData.deliveryInsights, 'deliveries'),
        hasSuccessRateData: hasData(analyticsData.deliveryInsights, 'successRate'),
        hasAvgTimeData: hasData(analyticsData.deliveryInsights, 'avgTime'),
        hasPatientData: hasData(analyticsData.patientDemographics, 'count'),
        hasRevenueData: hasData(analyticsData.revenueData, 'revenue'),
        hasMedicationData: hasData(analyticsData.medicationCategories, 'prescriptions')
      });
      
      console.log('Sample delivery insights data:', analyticsData.deliveryInsights?.slice(0, 2));
      console.log('Patient Demographics data:', analyticsData.patientDemographics);
      console.log('Satisfaction data:', analyticsData.satisfactionData);
      
    } catch (err) {
      setError(err.message);
      console.error('Error fetching analytics data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch data on component mount and when timeRange changes
  useEffect(() => {
    fetchAnalyticsData();
  }, [timeRange]);


  const exportReport = async () => {
    try {
      const res = await axiosInstance.get(`/analytics/export?timeRange=${timeRange}`, {
        responseType: "blob", // to download files
      });
  
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `analytics-report-${timeRange}.pdf`);
      document.body.appendChild(link);
      link.click();
    } catch (err) {
      console.error("Export failed:", err);
    }
  };


  // Loading state
  if (loading) {
    return (
      <GlobalLoader 
        isLoading={true} 
        message="Processing Please wait..." 
        subMessage="Please wait while we fetch your dashboard data" 
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          {/* <h1 className="text-3xl font-semibold text-gray-900">
            Admin Dashboard
          </h1> */}
          <p className="text-gray-600 mt-1">
            System overview and platform management
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          {/* <Button className="bg-blue-600 text-white hover:bg-blue-700">
            <Shield className="w-4 h-4 mr-2" />
            System Settings
          </Button> */}
          

<Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7d">Last 7 days</SelectItem>
              <SelectItem value="30d">Last 30 days</SelectItem>
              <SelectItem value="90d">Last 90 days</SelectItem>
              <SelectItem value="1y">Last year</SelectItem>
            </SelectContent>
          </Select>

          <Button
            className="h-11"
            variant="outline"
            onClick={() => setReportDialogOpen(true)}
          >
            <BarChart3 className="w-4 h-4 mr-2" />
            Generate Report
          </Button>

          {/* <Button variant="outline" onClick={exportReport} className="h-11">
            <Activity className="w-4 h-4 mr-2" />
            Export Report
          </Button> */}
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      <Card className="border-l-4 border-l-blue-500">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-600">
                  Total Pharmacies
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {adminStats.totalPharmacies}
                </p>
                <p className="text-xs text-blue-600 mt-1">
                  {/* {adminStats.activePharmacies} active */}
                </p>
              </div>
              <Building2 className="w-8 h-8 text-blue-600 flex-shrink-0" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-green-500">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">
                  Total Orders
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {overallStats?.totalOrders || 0}
                </p>
                <p className="text-xs text-green-600 mt-1">
                  {overallStats?.completedOrders || 0} completed
                </p>
              </div>
              <Package className="w-8 h-8 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-green-500">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-600">
                  Total Drivers
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {adminStats.totalDrivers}
                </p>
                <p className="text-xs text-green-600 mt-1">
                  {adminStats.activeDrivers} online
                </p>
              </div>
              <Users className="w-8 h-8 text-green-600 flex-shrink-0" />
            </div>
          </CardContent>
        </Card>
        

        {/* <Card className="border-l-4 border-l-purple-500">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">
                  Total Revenue
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  ${(overallStats?.totalRevenue || pharmacyStats?.totalRevenue || 0).toLocaleString()}
                </p>
                <p className="text-xs text-purple-600 mt-1">
                  {overallStats?.pendingOrders || 0} pending
                </p>
              </div>
              <DollarSign className="w-8 h-8 text-purple-600" />
            </div>
          </CardContent>
        </Card> */}


      </div>
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        

        

        {/* <Card className="border-l-4 border-l-purple-500">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-600">
                  Platform Orders
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {adminStats.totalOrders}
                </p>
                <p className="text-xs text-purple-600 mt-1">
                  {adminStats.todayOrders} today
                </p>
              </div>
              <Package className="w-8 h-8 text-purple-600 flex-shrink-0" />
            </div>
          </CardContent>
        </Card> */}

        

        {/* <Card className="border-l-4 border-l-orange-500">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-600">
                  System Health
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {adminStats.systemHealth}%
                </p>
                <p className="text-xs text-orange-600 mt-1">
                  All systems operational
                </p>
              </div>
              <Activity className="w-8 h-8 text-orange-600 flex-shrink-0" />
            </div>
          </CardContent>
        </Card> */}
      </div>

      {/* Revenue & Performance */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* <Card className="bg-gradient-to-r from-green-50 to-green-100 border-green-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-700 font-medium">Platform Revenue</p>
                <p className="text-2xl font-bold text-green-800">
                  ${adminStats.platformRevenue.toLocaleString()}
                </p>
                <p className="text-sm text-green-600">This month</p>
              </div>
              <TrendingUp className="w-8 h-8 text-green-600" />
            </div>
          </CardContent>
        </Card> */}

        {/* <Card className="bg-gradient-to-r from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-700 font-medium">Avg Order Value</p>
                <p className="text-2xl font-bold text-blue-800">
                  $
                  {(
                    adminStats.platformRevenue / adminStats.totalOrders
                  ).toFixed(2)}
                </p>
                <p className="text-sm text-blue-600">Across all pharmacies</p>
              </div>
              <BarChart3 className="w-8 h-8 text-blue-600" />
            </div>
          </CardContent>
        </Card> */}

        {/* <Card className="bg-gradient-to-r from-purple-50 to-purple-100 border-purple-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-700 font-medium">Database Status</p>
                <p className="text-2xl font-bold text-purple-800">Healthy</p>
                <p className="text-sm text-purple-600">
                  All connections stable
                </p>
              </div>
              <Database className="w-8 h-8 text-purple-600" />
            </div>
          </CardContent>
        </Card> */}
      </div>
      
      <Tabs defaultValue="pharmacy" className="space-y-6">
        <TabsList className="grid w-full grid-cols-1 bg-gray-100 p-1 rounded-lg">
          <TabsTrigger
            value="pharmacy"
            className="data-[state=active]:bg-white"
          >
            <Building2 className="w-4 h-4 mr-2" />
            Pharmacy Analytics
          </TabsTrigger>
          {/* <TabsTrigger
            value="delivery"
            className="data-[state=active]:bg-white"
          >
            <Truck className="w-4 h-4 mr-2" />
            Delivery Insights
          </TabsTrigger> */}
          {/* <TabsTrigger
            value="customers"
            className="data-[state=active]:bg-white"
          >
            <Users className="w-4 h-4 mr-2" />
            Patients/Customers
          </TabsTrigger> */}
          {/* <TabsTrigger
            value="operations"
            className="data-[state=active]:bg-white"
          >
            <Activity className="w-4 h-4 mr-2" />
            Operations
          </TabsTrigger> */}
          {/* <TabsTrigger value="revenue" className="data-[state=active]:bg-white">
            <DollarSign className="w-4 h-4 mr-2" />
            Revenue & Expenses
          </TabsTrigger> */}
          {/* <TabsTrigger value="orders" className="data-[state=active]:bg-white">
            <Package className="w-4 h-4 mr-2" />
            Orders & Deliveries
          </TabsTrigger> */}
        </TabsList>

        {/* Pharmacy Analytics Tab - NEW */}
        <TabsContent value="pharmacy" className="space-y-6">
        <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Users className="w-5 h-5 mr-2" />
                Detailed Pharmacy Analytics
              </CardTitle>
            </CardHeader>
            <CardContent>
              {pharmacyData.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr className="border-b">
                        <th className="text-left p-3 font-medium text-gray-700">Pharmacy</th>
                        <th className="text-left p-3 font-medium text-gray-700">Contact</th>
                        <th className="text-left p-3 font-medium text-gray-700">Status</th>
                        <th className="text-left p-3 font-medium text-gray-700">Orders</th>
                        {/* <th className="text-left p-3 font-medium text-gray-700">Pharmacy Revenue</th> */}
                        <th className="text-left p-3 font-medium text-gray-700">Completion</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pharmacyData.slice(0, 10).map((pharmacy, index) => (
                        <tr key={index} className="border-b hover:bg-gray-50 transition">
                          <td className="p-3">
                            <div>
                              <p className="font-medium text-gray-900">{pharmacy.pharmacyName}</p>
                              <p className="text-sm text-gray-600">{pharmacy.name}</p>
                            </div>
                          </td>
                          <td className="p-3">
                            <div>
                              <p className="text-sm text-gray-900">{pharmacy.email}</p>
                              <p className="text-sm text-gray-600">{pharmacy.phone}</p>
                            </div>
                          </td>
                          <td className="p-3">
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                              pharmacy.status === 'verified' 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {pharmacy.status}
                            </span>
                          </td>
                          <td className="p-3">
                            <div>
                              <p className="font-medium text-gray-900">{pharmacy.totalOrders}</p>
                              <p className="text-sm text-gray-600">{pharmacy.completedOrders} completed</p>
                            </div>
                          </td>
                          {/* <td className="p-3">
                            <div>
                              <p className="font-medium text-gray-900">${pharmacy.totalRevenue?.toLocaleString()}</p>
                              <p className="text-sm text-gray-600">${pharmacy.averageOrderValue?.toFixed(2)} avg</p>
                            </div>
                          </td> */}
                          <td className="p-3">
                            <div className="flex items-center gap-2">
                              <div className="flex-1 bg-gray-200 rounded-full h-2 max-w-[80px]">
                                <div 
                                  className="bg-green-600 h-2 rounded-full transition-all" 
                                  style={{ width: `${pharmacy.completionRate}%` }}
                                ></div>
                              </div>
                              <span className="text-sm font-medium text-gray-700">{pharmacy.completionRate}%</span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <Users className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <p>No pharmacy data available</p>
                </div>
              )}
            </CardContent>
          </Card>
          <div className="grid grid-cols-1 lg:grid-cols-1 gap-6">
            
            {/* Pharmacy Performance Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <BarChart3 className="w-5 h-5 mr-2" />
                  Top Pharmacy Performance
                </CardTitle>
              </CardHeader>
              <CardContent>
                {pharmacyAnalytics.length > 0 ? (
                  <div className="space-y-3">
                    {pharmacyAnalytics.slice(0, 5).map((pharmacy, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition">
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{pharmacy.name}</p>
                          <p className="text-sm text-gray-600">{pharmacy.orders} orders · {pharmacy.completionRate}% completion</p>
                        </div>
                        {/* <div className="text-right">
                          <p className="font-bold text-green-600">${pharmacy.revenue?.toLocaleString()}</p>
                        </div> */}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <BarChart3 className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                    <p>No pharmacy performance data available</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Revenue by Pharmacy Chart */}
            {/* <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <DollarSign className="w-5 h-5 mr-2" />
                  Revenue by Pharmacy
                </CardTitle>
              </CardHeader>
              <CardContent>
                {pharmacyRevenue.length > 0 ? (
                  <div className="space-y-3">
                    {pharmacyRevenue.slice(0, 5).map((pharmacy, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition">
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{pharmacy.name}</p>
                          <p className="text-sm text-gray-600">{pharmacy.orders} orders · ${pharmacy.averageOrderValue?.toFixed(2)} avg</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-blue-600">${pharmacy.revenue?.toLocaleString()}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <DollarSign className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                    <p>No revenue data available</p>
                  </div>
                )}
              </CardContent>
            </Card> */}
          </div>

          {/* Growth and Performance Metrics */}
          {/* <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <TrendingUp className="w-5 h-5 mr-2" />
                  Growth Metrics
                </CardTitle>
              </CardHeader>
              <CardContent>
                {pharmacyGrowth.length > 0 ? (
                  <div className="space-y-3">
                    {pharmacyGrowth.slice(0, 5).map((pharmacy, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition">
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{pharmacy.name}</p>
                          <p className="text-sm text-gray-600">{pharmacy.orders} orders · ${pharmacy.revenue?.toLocaleString()}</p>
                        </div>
                        <div className="text-right">
                          <p className={`font-bold text-lg ${pharmacy.growth >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {pharmacy.growth >= 0 ? '+' : ''}{pharmacy.growth}%
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <TrendingUp className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                    <p>No growth data available</p>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Activity className="w-5 h-5 mr-2" />
                  Performance Efficiency
                </CardTitle>
              </CardHeader>
              <CardContent>
                {pharmacyPerformance.length > 0 ? (
                  <div className="space-y-3">
                    {pharmacyPerformance.slice(0, 5).map((pharmacy, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition">
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{pharmacy.name}</p>
                          <p className="text-sm text-gray-600">{pharmacy.orders} orders · ${pharmacy.revenue?.toLocaleString()}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-lg text-purple-600">{pharmacy.efficiency}%</p>
                          <p className="text-xs text-gray-500">efficiency</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <Activity className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                    <p>No performance data available</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div> */}

          {/* Pharmacy Data Table */}
          
        </TabsContent>
{/* 
        <TabsContent value="delivery" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Weekly Delivery Performance</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  {hasData(deliveryInsightsData, 'deliveries') ? (
                    <ChartContainer config={chartConfig} className="h-full">
                      <BarChart data={deliveryInsightsData} width={400} height={300}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e0e4e7" />
                        <XAxis 
                          dataKey="day" 
                          tick={{ fontSize: 12, fill: '#6b7280' }}
                          axisLine={{ stroke: '#d1d5db' }}
                        />
                        <YAxis 
                          tick={{ fontSize: 12, fill: '#6b7280' }}
                          axisLine={{ stroke: '#d1d5db' }}
                        />
                        <ChartTooltip 
                          content={<ChartTooltipContent hideLabel={false}  />}
                          cursor={false}
                        />
                        <Bar
                          dataKey="deliveries"
                          fill="#3b82f6"
                          stroke="#2563eb"
                          strokeWidth={1}
                          radius={[4, 4, 0, 0]}
                        />
                      </BarChart>
                    </ChartContainer>
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <p className="text-gray-500">No delivery data available</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Success Rate Trends</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  {hasData(deliveryInsightsData, 'successRate') ? (
                    <ChartContainer config={chartConfig} className="h-full">
                      <LineChart data={deliveryInsightsData} width={400} height={300}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e0e4e7" />
                        <XAxis 
                          dataKey="day" 
                          tick={{ fontSize: 12, fill: '#6b7280' }}
                          axisLine={{ stroke: '#d1d5db' }}
                        />
                        <YAxis 
                          tick={{ fontSize: 12, fill: '#6b7280' }}
                          axisLine={{ stroke: '#d1d5db' }}
                        />
                        <ChartTooltip 
                          content={<ChartTooltipContent hideLabel={false} />}
                          cursor={{ stroke: 'rgba(16, 185, 129, 0.3)' }}
                        />
                        <Line
                          type="monotone"
                          dataKey="successRate"
                          stroke="#10b981"
                          strokeWidth={4}
                          strokeOpacity={1}
                          dot={{ fill: '#10b981', strokeWidth: 2, r: 5, fillOpacity: 1 }}
                          activeDot={{ r: 8, fill: '#059669', strokeWidth: 2, stroke: '#fff' }}
                          connectNulls={false}
                        />
                      </LineChart>
                    </ChartContainer>
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <p className="text-gray-500">No success rate data available</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Average Delivery Times</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  {hasData(deliveryInsightsData, 'avgTime') ? (
                    <ChartContainer config={chartConfig} className="h-full">
                      <AreaChart data={deliveryInsightsData} width={400} height={300}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e0e4e7" />
                        <XAxis 
                          dataKey="day" 
                          tick={{ fontSize: 12, fill: '#6b7280' }}
                          axisLine={{ stroke: '#d1d5db' }}
                        />
                        <YAxis 
                          tick={{ fontSize: 12, fill: '#6b7280' }}
                          axisLine={{ stroke: '#d1d5db' }}
                        />
                        <ChartTooltip 
                          content={<ChartTooltipContent hideLabel={false} />}
                          cursor={{ stroke: 'rgba(245, 158, 11, 0.3)' }}
                        />
                        <Area
                          type="monotone"
                          dataKey="avgTime"
                          stroke="#f59e0b"
                          strokeWidth={4}
                          strokeOpacity={1}
                          fill="#f59e0b"
                          fillOpacity={0.3}
                          dot={{ fill: '#f59e0b', strokeWidth: 2, r: 5, fillOpacity: 1 }}
                          activeDot={{ r: 8, fill: '#d97706', strokeWidth: 2, stroke: '#fff' }}
                          connectNulls={false}
                        />
                      </AreaChart>
                    </ChartContainer>
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <p className="text-gray-500">No delivery time data available</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Delivery Status Overview</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  {orderStatusData && orderStatusData.length > 0 ? (
                    <ChartContainer config={chartConfig} className="h-full">
                      <PieChart width={400} height={300}>
                        <Pie
                          data={orderStatusData}
                          cx="50%"
                          cy="50%"
                          outerRadius={100}
                          innerRadius={40}
                          dataKey="value"
                          label={({ name, value, percent }) => 
                            `${name}: ${value} (${(percent * 100).toFixed(0)}%)`
                          }
                          labelLine={false}
                        >
                          {orderStatusData.map((entry, index) => (
                            <Cell 
                              key={`cell-${index}`} 
                              fill={entry.color} 
                              stroke="#fff"
                              strokeWidth={2}
                            />
                          ))}
                        </Pie>
                        <ChartTooltip 
                          content={<ChartTooltipContent hideLabel={true} />}
                        />
                      </PieChart>
                    </ChartContainer>
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <p className="text-gray-500">No order status data available</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent> */}

        {/* <TabsContent value="customers" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Customer Demographics</CardTitle>
                <div className="text-xs text-gray-500 mt-1">
                  Data: {patientDemographics?.map(d => `${d.age}: ${d.count}`).join(', ')}
                </div>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  {patientDemographics && patientDemographics.length > 0 ? (
                    <ChartContainer config={chartConfig} className="h-full">
                      <BarChart 
                        data={patientDemographics} 
                        layout="horizontal"
                        width={400} 
                        height={300}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#e0e4e7" />
                        <XAxis 
                          dataKey="age" 
                          type="category" 
                          tick={{ fontSize: 12, fill: '#6b7280' }}
                          axisLine={{ stroke: '#d1d5db' }}
                        />
                        <YAxis 
                          type="number" 
                          domain={[0, 'dataMax + 1']}
                          tick={{ fontSize: 12, fill: '#6b7280' }}
                          axisLine={{ stroke: '#d1d5db' }}
                        />
                        <ChartTooltip 
                          content={<ChartTooltipContent hideLabel={false} />}
                          cursor={false}
                        />
                        <Bar
                          dataKey="count"
                          fill="#3b82f6"
                          stroke="#2563eb"
                          strokeWidth={2}
                          radius={[4, 4, 0, 0]}
                          minPointSize={5}
                        />
                      </BarChart>
                    </ChartContainer>
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <p className="text-gray-500">No patient demographic data available</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Customer Satisfaction Ratings</CardTitle>
                <div className="text-xs text-gray-500 mt-1">
                  Data: {satisfactionData?.map(d => `${d.category}: ${d.rating}`).join(', ')}
                </div>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  {satisfactionData && satisfactionData.length > 0 ? (
                    <ChartContainer config={chartConfig} className="h-full">
                      <BarChart 
                        data={satisfactionData} 
                        layout="horizontal"
                        width={400} 
                        height={300}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#e0e4e7" />
                       

                        <XAxis 
                          dataKey="category" 
                          type="category" 
                          tick={{ fontSize: 12, fill: '#6b7280' }}
                          axisLine={{ stroke: '#d1d5db' }}
                        />
                        <YAxis 
                          type="number" 
                          domain={[0, 5]} 
                          tick={{ fontSize: 12, fill: '#6b7280' }}
                          axisLine={{ stroke: '#d1d5db' }}
                          width={120}
                        />
                        <ChartTooltip 
                          content={<ChartTooltipContent hideLabel={false} />}
                          cursor={false}
                        />
                        <Bar
                          dataKey="rating"
                          fill="#10b981"
                          stroke="#059669"
                          strokeWidth={1}
                          radius={[0, 4, 4, 0]}
                        />
                      </BarChart>
                    </ChartContainer>
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <p className="text-gray-500">No satisfaction data available</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Customer Retention</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center">
                  {customerRetention ? (
                    <>
                      <div className="text-3xl font-bold text-green-600 mb-2">
                        {customerRetention.retentionRate}%
                      </div>
                      <p className="text-sm text-gray-600">
                        Customer Retention Rate
                      </p>
                      <div className="mt-4 space-y-2">
                        <div className="flex justify-between text-sm">
                          <span>New Customers</span>
                          <span className="font-medium">{customerRetention.newCustomers}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span>Returning Customers</span>
                          <span className="font-medium">{customerRetention.returningCustomers}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span>Total Customers</span>
                          <span className="font-medium">{customerRetention.totalCustomers}</span>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="text-3xl font-bold text-gray-400 mb-2">
                        N/A
                      </div>
                      <p className="text-sm text-gray-600">
                        Customer Retention Rate
                      </p>
                      <p className="text-xs text-gray-500 mt-2">
                        Data not available
                      </p>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Top Customer Segments</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Heart className="w-4 h-4 text-red-500 mr-2" />
                      <span className="text-sm">Chronic Care</span>
                    </div>
                    <Badge variant="outline">45%</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Pill className="w-4 h-4 text-blue-500 mr-2" />
                      <span className="text-sm">Acute Care</span>
                    </div>
                    <Badge variant="outline">32%</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <ShoppingCart className="w-4 h-4 text-green-500 mr-2" />
                      <span className="text-sm">OTC Products</span>
                    </div>
                    <Badge variant="outline">23%</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Monthly Growth</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>New Patient Registrations</span>
                      <span className="font-medium text-green-600">+18%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-green-500 h-2 rounded-full"
                        style={{ width: "0%" }}
                      ></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>Prescription Refills</span>
                      {operationalKPIs ? (
                        <span className="font-medium text-blue-600">{operationalKPIs.prescriptionRefillRate}%</span>
                      ) : (
                        <span className="font-medium text-gray-400">N/A</span>
                      )}
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-500 h-2 rounded-full"
                        style={{ width: operationalKPIs ? `${operationalKPIs.prescriptionRefillRate}%` : "0%" }}
                      ></div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent> */}

       
        {/* <TabsContent value="operations" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Operational KPIs</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Target className="w-4 h-4 text-blue-500 mr-2" />
                      
                    </div>
                    <span className="font-bold text-blue-600">12 min</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Zap className="w-4 h-4 text-green-500 mr-2" />
                      <span className="text-sm">Efficiency Rate</span>
                    </div>
                    <span className="font-bold text-green-600">94%</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Clock className="w-4 h-4 text-orange-500 mr-2" />
                      <span className="text-sm">Peak Hours</span>
                    </div>
                    <span className="font-bold text-orange-600">2-4 PM</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Staff Performance</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Pharmacist Efficiency</span>
                    <Badge className="bg-green-100 text-green-800">98%</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Technician Speed</span>
                    <Badge className="bg-blue-100 text-blue-800">92%</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Quality Control</span>
                    <Badge className="bg-purple-100 text-purple-800">99%</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Resource Utilization</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>Equipment Usage</span>
                      <span className="font-medium text-gray-400">N/A</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-500 h-2 rounded-full"
                        style={{ width: "0%" }} 
                      ></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>Equipment Usage</span>
                      {equipmentUsage ? (
                        <span className="font-medium">{equipmentUsage.usageRate}%</span>
                      ) : (
                        <span className="font-medium text-gray-400">N/A</span>
                      )}
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-green-500 h-2 rounded-full"
                        style={{ width: equipmentUsage ? `${equipmentUsage.usageRate}%` : "0%" }}
                      ></div>
                    </div>
                    {equipmentUsage && (
                      <div className="mt-2 text-xs text-gray-500">
                        {equipmentUsage.activeEquipment}/{equipmentUsage.totalEquipment} {equipmentUsage.equipmentType}
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent> */}

        <TabsContent value="revenue" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Revenue vs Expenses</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  {revenueData && revenueData.length > 0 ? (
                    <ChartContainer config={chartConfig} className="h-full">
                      <ComposedChart data={revenueData} width={400} height={300}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e0e4e7" />
                        <XAxis 
                          dataKey="month" 
                          tick={{ fontSize: 12, fill: '#6b7280' }}
                          axisLine={{ stroke: '#d1d5db' }}
                        />
                        <YAxis 
                          tick={{ fontSize: 12, fill: '#6b7280' }}
                          axisLine={{ stroke: '#d1d5db' }}
                        />
                        <ChartTooltip 
                          content={<ChartTooltipContent hideLabel={false} />}
                          cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }}
                        />
                        <Bar
                          dataKey="revenue"
                          fill="#3b82f6"
                          stroke="#2563eb"
                          strokeWidth={1}
                          radius={[2, 2, 0, 0]}
                        />
                        <Bar
                          dataKey="expenses"
                          fill="#ef4444"
                          stroke="#dc2626"
                          strokeWidth={1}
                          radius={[2, 2, 0, 0]}
                        />
                        <Line
                          type="monotone"
                          dataKey="profit"
                          stroke="#10b981"
                          strokeWidth={4}
                          strokeOpacity={1}
                          dot={{ fill: '#10b981', strokeWidth: 2, r: 5, fillOpacity: 1 }}
                          activeDot={{ r: 8, fill: '#059669', strokeWidth: 2, stroke: '#fff' }}
                          connectNulls={false}
                        />
                      </ComposedChart>
                    </ChartContainer>
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <p className="text-gray-500">No revenue data available</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Profit Trends</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  {hasData(revenueData, 'profit') ? (
                    <ChartContainer config={chartConfig} className="h-full">
                      <AreaChart data={revenueData} width={400} height={300}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e0e4e7" />
                        <XAxis 
                          dataKey="month" 
                          tick={{ fontSize: 12, fill: '#6b7280' }}
                          axisLine={{ stroke: '#d1d5db' }}
                        />
                        <YAxis 
                          tick={{ fontSize: 12, fill: '#6b7280' }}
                          axisLine={{ stroke: '#d1d5db' }}
                        />
                        <ChartTooltip 
                          content={<ChartTooltipContent hideLabel={false} />}
                          cursor={{ stroke: 'rgba(16, 185, 129, 0.3)' }}
                        />
                        <Area
                          type="monotone"
                          dataKey="profit"
                          stroke="#10b981"
                          strokeWidth={4}
                          strokeOpacity={1}
                          fill="#10b981"
                          fillOpacity={0.3}
                          dot={{ fill: '#10b981', strokeWidth: 2, r: 5, fillOpacity: 1 }}
                          activeDot={{ r: 8, fill: '#059669', strokeWidth: 2, stroke: '#fff' }}
                          connectNulls={false}
                        />
                      </AreaChart>
                    </ChartContainer>
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <p className="text-gray-500">No profit trend data available</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Revenue Breakdown</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Pill className="w-4 h-4 text-blue-500 mr-2" />
                      <span className="text-sm">Prescription Sales</span>
                    </div>
                    <span className="font-bold text-blue-600">$12,450</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <ShoppingCart className="w-4 h-4 text-green-500 mr-2" />
                      <span className="text-sm">OTC Products</span>
                    </div>
                    <span className="font-bold text-green-600">$3,200</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Truck className="w-4 h-4 text-purple-500 mr-2" />
                      <span className="text-sm">Delivery Fees</span>
                    </div>
                    <span className="font-bold text-purple-600">$1,850</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Expense Categories</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Staff Salaries</span>
                    <span className="font-bold text-red-600">$4,200</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Inventory</span>
                    <span className="font-bold text-orange-600">$3,800</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Operations</span>
                    <span className="font-bold text-yellow-600">$1,200</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Financial Health</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    ${revenueData.length > 0 
                      ? revenueData[revenueData.length - 1]?.profit?.toLocaleString() || '0'
                      : '0'
                    }
                  </div>
                  <p className="text-sm text-gray-600 mb-4">Monthly Profit</p>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Profit Margin</span>
                      {growthMetrics ? (
                        <span className="font-medium text-green-600">+{growthMetrics.revenueGrowth}%</span>
                      ) : (
                        <span className="font-medium text-gray-400">N/A</span>
                      )}
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Growth Rate</span>
                      <span className="font-medium text-blue-600">+18%</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Orders & Deliveries Tab */}
        <TabsContent value="orders" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Order Volume Trends</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  {hasData(deliveryInsightsData, 'deliveries') ? (
                    <ChartContainer config={chartConfig} className="h-full">
                      <LineChart data={deliveryInsightsData} width={400} height={300}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e0e4e7" />
                        <XAxis 
                          dataKey="day" 
                          tick={{ fontSize: 12, fill: '#6b7280' }}
                          axisLine={{ stroke: '#d1d5db' }}
                        />
                        <YAxis 
                          tick={{ fontSize: 12, fill: '#6b7280' }}
                          axisLine={{ stroke: '#d1d5db' }}
                        />
                        <ChartTooltip 
                          content={<ChartTooltipContent hideLabel={false} />}
                          cursor={{ stroke: 'rgba(59, 130, 246, 0.3)' }}
                        />
                        <Line
                          type="monotone"
                          dataKey="deliveries"
                          stroke="#3b82f6"
                          strokeWidth={4}
                          strokeOpacity={1}
                          dot={{ fill: '#3b82f6', strokeWidth: 2, r: 5, fillOpacity: 1 }}
                          activeDot={{ r: 8, fill: '#2563eb', strokeWidth: 2, stroke: '#fff' }}
                          connectNulls={false}
                        />
                      </LineChart>
                    </ChartContainer>
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <p className="text-gray-500">No order volume data available</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Medication Categories</CardTitle>
              </CardHeader>
              <CardContent>
                <ChartContainer config={chartConfig}>
                  <BarChart
                    data={medicationCategories}
                    layout="horizontal" // ✅ use vertical for horizontal bars
                  >
                    <CartesianGrid strokeDasharray="2 2" />
                    <XAxis dataKey="category" type="category" />
                    <YAxis type="number" />
                    <ChartTooltip
                      cursor={false}
                      content={<ChartTooltipContent />}
                    />
                    <Bar
                      dataKey="prescriptions"
                      fill="#f59e0b"
                      stroke="#d97706"
                      strokeWidth={1}
                    />
                  </BarChart>
                </ChartContainer>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Order Status Overview</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {orderStatusData.map((status, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between"
                    >
                      <div className="flex items-center">
                        <div
                          className="w-3 h-3 rounded-full mr-2"
                          style={{ backgroundColor: status.color }}
                        ></div>
                        <span className="text-sm">{status.name}</span>
                      </div>
                      <span className="font-bold">{status.value}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Delivery Performance</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm">On-Time Delivery</span>
                    {deliveryTypeBreakdown ? (
                      <Badge className="bg-green-100 text-green-800">
                        {deliveryTypeBreakdown.sameDay?.successRate?.toFixed(1) || 'N/A'}%
                      </Badge>
                    ) : (
                      <Badge className="bg-gray-100 text-gray-500">N/A</Badge>
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Same-Day Delivery</span>
                    {deliveryTypeBreakdown ? (
                      <Badge className="bg-blue-100 text-blue-800">
                        {deliveryTypeBreakdown.sameDay?.successRate?.toFixed(1) || 'N/A'}%
                      </Badge>
                    ) : (
                      <Badge className="bg-gray-100 text-gray-500">N/A</Badge>
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Next-Day Delivery</span>
                    {deliveryTypeBreakdown ? (
                      <Badge className="bg-purple-100 text-purple-800">
                        {deliveryTypeBreakdown.nextDay?.successRate?.toFixed(1) || 'N/A'}%
                      </Badge>
                    ) : (
                      <Badge className="bg-gray-100 text-gray-500">N/A</Badge>
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Failed Deliveries</span>
                    <Badge className="bg-red-100 text-red-800">1.9%</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Top Performing Medications</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {medicationCategories.slice(0, 3).map((med, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between"
                    >
                      <div>
                        <p className="text-sm font-medium">{med.category}</p>
                        <p className="text-xs text-gray-500">
                          {med.prescriptions} prescriptions
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold text-green-600">
                          ${med.revenue.toLocaleString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
      
      {/* <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Recent System Activity</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {recentActivity.map((activity) => (
                <div key={activity.id} className="flex items-start space-x-3">
                  <div
                    className={`w-2 h-2 rounded-full mt-2 ${
                      activity.type === "pharmacy"
                        ? "bg-blue-500"
                        : activity.type === "driver"
                        ? "bg-green-500"
                        : activity.type === "system"
                        ? "bg-orange-500"
                        : "bg-purple-500"
                    }`}
                  />
                  <div className="flex-1">
                    <p className="text-sm text-gray-900">{activity.action}</p>
                    <p className="text-xs text-gray-500">{activity.time}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Pharmacies</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {topPerformingPharmacies.map((pharmacy, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Building2 className="w-4 h-4 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">
                        {pharmacy.name}
                      </p>
                      <p className="text-sm text-gray-500">
                        {pharmacy.orders} orders
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-green-600">
                      ${pharmacy?.revenue ? pharmacy?.revenue.toLocaleString() : 0}
                    </p>
                    <Badge variant="outline" className="text-xs">
                      #{index + 1}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div> */}

      {/* Generate Report Dialog */}
      <Dialog open={reportDialogOpen} onOpenChange={setReportDialogOpen}>
        <DialogContent className="w-[600px] bg-white" style={{ backgroundColor: 'white' }}>
          <DialogHeader className="bg-white" style={{ backgroundColor: 'white' }}>
            <DialogTitle>Generate Report</DialogTitle>
            <DialogDescription>
              Select the report type and format to generate and download.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4 bg-white" style={{ backgroundColor: 'white' }}>
            <div className="space-y-2">
              <Label htmlFor="report-type">Report Type</Label>
              <Select 
                value={reportType} 
                onValueChange={(value: any) => setReportType(value)}
              >
                <SelectTrigger id="report-type">
                  <SelectValue placeholder="Select report type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="orders">
                    <div className="flex items-center gap-2">
                      <Package className="w-4 h-4" />
                      <span>Orders Report</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="routes">
                    <div className="flex items-center gap-2">
                      <BarChart3 className="w-4 h-4" />
                      <span>Routes Report</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="audit">
                    <div className="flex items-center gap-2">
                      <Shield className="w-4 h-4" />
                      <span>Audit Report</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="report-format">Format</Label>
              <Select 
                value={reportFormat} 
                onValueChange={(value: any) => setReportFormat(value)}
              >
                <SelectTrigger id="report-format">
                  <SelectValue placeholder="Select format" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="csv">
                    <div className="flex items-center gap-2">
                      <Download className="w-4 h-4" />
                      <span>CSV (.csv)</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="pdf">
                    <div className="flex items-center gap-2">
                      <Download className="w-4 h-4" />
                      <span>PDF (.pdf)</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
              <p className="font-medium mb-1">📊 Report Preview</p>
              <p>
                {reportType === 'orders' && 'All orders with pharmacy, driver, and delivery details'}
                {reportType === 'routes' && 'All delivery routes with waypoints and assignments'}
                {reportType === 'audit' && 'System audit logs and user activities'}
              </p>
            </div>
          </div>

          <DialogFooter className="bg-white" style={{ backgroundColor: 'white' }}>
            <Button 
              variant="outline" 
              onClick={() => setReportDialogOpen(false)}
              disabled={generatingReport}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleGenerateReport}
              disabled={generatingReport}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              <Download className="w-4 h-4 mr-2" />
              Generate & Download
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
